import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TriangleAlertIcon } from "lucide-react";
import { useAuth } from "@/client/lib/auth-context";
import { Alert, AlertDescription, AlertTitle } from "@/client/components/base/alert";
import { Button } from "@/client/components/base/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/client/components/base/card";
import PasswordInput from "@/client/components/derived/PasswordInput";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/client/components/base/field";
import { Input } from "@/client/components/base/input";
import { Spinner } from "@/client/components/base/spinner";

type Errors = {
    username?: string;
    password?: string;
};

const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState<Errors>({});
    const [formError, setFormError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isComplete = username.trim().length > 0 && password.length > 0;

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const newErrors: Errors = {};
        if (username.trim().length === 0) {
            newErrors.username = "Username is required";
        }
        if (password.length === 0) {
            newErrors.password = "Password is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        setFormError(null);

        const result = await login(username, password);

        setIsSubmitting(false);

        if (result.error) {
            setFormError(result.error);
            return;
        }

        navigate("/me");
    };

    return (
        <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-background p-4 sm:p-8">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 [background:radial-gradient(70%_60%_at_50%_-10%,var(--primary)_0%,transparent_60%)] opacity-10"
            />
            <div className="relative flex w-full max-w-md flex-col gap-4">
                <Card>
                    <CardHeader>
                        <CardTitle>Welcome back</CardTitle>
                        <CardDescription>Log in with your username and password.</CardDescription>
                    </CardHeader>

                    {formError && (
                        <div className="px-4 sm:px-6">
                            <Alert variant="destructive">
                                <TriangleAlertIcon aria-hidden="true" />
                                <AlertTitle>Login failed</AlertTitle>
                                <AlertDescription>{formError}</AlertDescription>
                            </Alert>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                        <CardContent>
                            <FieldGroup>
                                <Field data-invalid={!!errors.username}>
                                    <FieldLabel htmlFor="username">Username</FieldLabel>
                                    <Input
                                        id="username"
                                        value={username}
                                        placeholder="e.g. alpha_trader"
                                        autoComplete="username"
                                        aria-invalid={!!errors.username}
                                        onChange={(event) => {
                                            setUsername(event.target.value);
                                            setErrors((current) => ({ ...current, username: undefined }));
                                            setFormError(null);
                                        }}
                                    />
                                    <FieldError
                                        errors={errors.username ? [{ message: errors.username }] : undefined}
                                    />
                                </Field>
                                <Field data-invalid={!!errors.password}>
                                    <FieldLabel htmlFor="password">Password</FieldLabel>
                                    <PasswordInput
                                        id="password"
                                        value={password}
                                        placeholder="Your password"
                                        invalid={!!errors.password}
                                        autoComplete="current-password"
                                        onChange={(value) => {
                                            setPassword(value);
                                            setErrors((current) => ({ ...current, password: undefined }));
                                            setFormError(null);
                                        }}
                                    />
                                    <FieldError
                                        errors={errors.password ? [{ message: errors.password }] : undefined}
                                    />
                                </Field>
                            </FieldGroup>
                        </CardContent>

                        <CardFooter className="flex-col gap-3">
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={!isComplete || isSubmitting}
                            >
                                {isSubmitting ? <Spinner data-icon="inline-start" /> : null}
                                {isSubmitting ? "Logging in..." : "Log in"}
                            </Button>
                            <p className="text-center text-sm text-muted-foreground">
                                Don't have an account?{" "}
                                <Link
                                    to="/register"
                                    className="font-medium text-primary underline-offset-4 hover:underline"
                                >
                                    Register
                                </Link>
                            </p>
                        </CardFooter>
                    </form>
                </Card>
            </div>
        </main>
    );
};

export default LoginPage;
